/**
 * BottomNav - Bottom tab navigation bar (compact with center FAB)
 */
import React from 'react';
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Icon, IconName } from './Icon';
import { colors, SAFE_BOTTOM } from '../theme';
import type { RootStackParamList } from '../types/navigation';

type TabItem = {
  id: keyof RootStackParamList;
  label: string;
  icon: IconName;
  filledIcon?: IconName;
};

const LEFT_TABS: TabItem[] = [
  { id: 'M03HomeScreen',    label: 'Trang chủ', icon: 'home',    filledIcon: 'home' },
  { id: 'M11HistoryScreen', label: 'Lịch sử',   icon: 'history', filledIcon: 'history' },
];

const RIGHT_TABS: TabItem[] = [
  { id: 'M12ProfileScreen',     label: 'Hồ sơ',   icon: 'person',   filledIcon: 'person' },
  { id: 'M04ProductListScreen', label: 'Sản phẩm', icon: 'grid_view', filledIcon: 'grid_view' },
];

const NAV_BG = 'rgba(255,255,255,0.96)';

export const BottomNav: React.FC = () => {
  const navigation = useNavigation<any>();
  const insets = useSafeAreaInsets();
  const bottomPad = Math.max(insets.bottom, SAFE_BOTTOM);

  const navState = (navigation as any).getState?.();
  const screenName = navState?.routes?.[navState?.index ?? 0]?.name as keyof RootStackParamList | undefined;

  const showNav = screenName && screenName !== 'M01SplashScreen' && screenName !== 'M02LoginScreen';
  if (!showNav) return null;

  const getActiveTab = (currentScreen: string): string => {
    if (currentScreen === 'M03HomeScreen') return 'M03HomeScreen';
    if (
      [
        'M04ProductListScreen',
        'M05ProductDetailScreen',
        'M06SelectCustomerScreen',
        'M06ConfigureProductScreen',
        'M06InputCustomerScreen',
        'M07ResultScreen',
        'M08CompareScreen',
        'M09PdfScreen',
        'M10SaveCustomerScreen',
      ].includes(currentScreen)
    ) {
      return 'M04ProductListScreen';
    }
    if (currentScreen === 'M11HistoryScreen') return 'M11HistoryScreen';
    if (currentScreen === 'M12ProfileScreen') return 'M12ProfileScreen';
    return '';
  };

  const activeTabId = screenName ? getActiveTab(screenName) : '';

  const renderTab = (tab: TabItem) => {
    const isActive = activeTabId === tab.id;
    return (
      <TouchableOpacity
        key={tab.id}
        style={[styles.item, isActive && styles.itemActive]}
        onPress={() => navigation.navigate(tab.id)}
        activeOpacity={0.7}
        accessibilityRole="tab"
        aria-selected={isActive}
      >
        <Icon
          name={isActive ? (tab.filledIcon ?? tab.icon) : tab.icon}
          size={20}
          color={isActive ? colors.secondary : '#bdbdbd'}
          filled={isActive}
        />
        <Text style={[styles.label, isActive ? styles.labelActive : styles.labelInactive]}>
          {tab.label}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <View
      style={[
        styles.wrap,
        {
          paddingBottom: bottomPad,
          backgroundColor: NAV_BG,
          borderTopWidth: StyleSheet.hairlineWidth,
          borderTopColor: 'rgba(0,51,102,0.08)',
        },
      ]}
    >
      {/* Left tabs */}
      <View style={styles.tabGroup}>
        {LEFT_TABS.map(renderTab)}
      </View>

      {/* Center FAB */}
      <View style={styles.fabWrap}>
        <TouchableOpacity
          style={styles.fab}
          onPress={() => navigation.navigate('M04ProductListScreen')}
          activeOpacity={0.85}
          accessibilityRole="button"
          accessibilityLabel="Tạo minh họa mới"
        >
          <Icon name="add" size={26} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.fabLabel}>Tạo minh họa</Text>
      </View>

      {/* Right tabs */}
      <View style={styles.tabGroup}>
        {RIGHT_TABS.map(renderTab)}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    paddingTop: 8,
    paddingHorizontal: 8,
    // @ts-ignore backdropFilter is a web-only property
    backdropFilter: 'blur(20px)',
    elevation: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
  },
  tabGroup: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  item: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 10,
    flex: 1,
  },
  itemActive: {
    backgroundColor: 'rgba(0,166,81,0.07)',
  },
  label: {
    fontFamily: "'Roboto', sans-serif",
    fontSize: 9,
    fontWeight: '600',
    letterSpacing: 0.2,
    textTransform: 'uppercase',
    marginTop: 2,
  },
  labelActive: {
    color: colors.secondary,
  },
  labelInactive: {
    color: '#bdbdbd',
  },
  /* Center FAB */
  fabWrap: {
    alignItems: 'center',
    paddingHorizontal: 4,
    marginBottom: Platform.OS === 'web' ? 4 : 0,
  },
  fab: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: colors.secondary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 2,
    marginTop: -20,
    shadowColor: colors.secondary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 8,
    borderWidth: 3,
    borderColor: '#ffffff',
  },
  fabLabel: {
    fontFamily: "'Roboto', sans-serif",
    fontSize: 9,
    fontWeight: '700',
    color: colors.secondary,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
    marginTop: 1,
  },
});
